import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Search } from "lucide-react";
import SwipeIcon from "../assets/helperIcons/SwipeIcon";

export default function ProductSelector({
  products = [],
  selected,
  disabled = false,
  onChange,
  formatCurrency,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0, openUpward: false });
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);

  const selectedProduct = products.find((p) => p._id === selected);

  const filteredProducts = products.filter((product) => {
    const query = searchQuery.trim().toLowerCase()
    return [product.name, product.sku, product.description]
      .filter(Boolean)
      .some(value => String(value).toLowerCase().includes(query))
  });

  // Calculate position when opening
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const dropdownHeight = 350; // Approximate max height of dropdown
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const openUpward = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;

      setPosition({
        top: openUpward ? rect.top - dropdownHeight : rect.bottom + 4,
        left: rect.left,
        width: Math.max(rect.width, 300),
        openUpward,
      });
    }
  }, [isOpen]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        setSearchQuery("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const handleSelect = (productId) => {
    onChange(productId);
    setIsOpen(false);
    setSearchQuery("");
  };

  const DropdownContent = () => (
    <div
      ref={dropdownRef}
      style={{
        position: "fixed",
        top: `${position.top}px`,
        left: `${position.left}px`,
        width: `${position.width}px`,
        zIndex: 99999,
      }}
      className="bg-white border border-gray-300 rounded-lg shadow-2xl overflow-hidden"
    >
      {/* Search */}
      <div className="p-3 border-b border-gray-200 bg-gray-50">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search products..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            autoFocus
          />
        </div>
      </div>

      {/* Product List */}
      <div className="max-h-64 overflow-y-auto">
        {filteredProducts.length === 0 ? (
          <div className="px-4 py-8 text-center text-gray-500 text-sm">
            No products found
          </div>
        ) : (
          filteredProducts.map((product) => (
            <div
              key={product._id}
              onClick={() => handleSelect(product._id)}
              className="px-4 py-3 hover:bg-purple-50 cursor-pointer transition-colors border-b border-gray-100 last:border-b-0"
            >
              <div className="flex justify-between items-center">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">
                    {product.name}
                  </div>
                  {product.sku && (
                    <div className="text-xs text-gray-500 mt-0.5">
                      SKU: {product.sku}
                    </div>
                  )}
                </div>
                <div className="ml-3 text-sm font-semibold text-purple-600 whitespace-nowrap">
                  {formatCurrency(product.price)}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add New Product */}
      <div
        onClick={() => handleSelect("add_new")}
        className="px-4 py-3 bg-blue-50 hover:bg-blue-100 cursor-pointer border-t-2 border-blue-200 transition-colors"
      >
        <div className="text-sm font-medium text-blue-700">
          + Add New Product
        </div>
      </div>
    </div>
  );

  return (
    <div className="w-full">
      <div ref={buttonRef} className="flex items-center gap-2">
        <SwipeIcon />
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className={`flex-1 flex items-center justify-between px-2 py-1.5 text-sm rounded border transition-colors ${
            disabled
              ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200"
              : "bg-white hover:border-purple-400 border-gray-300 text-gray-900"
          }`}
        >
          <span className="truncate">
            {disabled
              ? "Package Item"
              : selectedProduct
              ? selectedProduct.name
              : "Select Product"}
          </span>
          {!disabled && (
            <ChevronDown
              className={`h-4 w-4 ml-2 transition-transform flex-shrink-0 ${
                isOpen ? "rotate-180" : ""
              }`}
            />
          )}
        </button>
      </div>

      {isOpen && !disabled && createPortal(<DropdownContent />, document.body)}
    </div>
  );
}
